import { ReadonlyMat3, ReadonlyVec3, mat3, vec3 } from 'gl-matrix';

/**
 * Anatomical direction label in DICOM LPS convention.
 */
export type DirectionLabel = 'L' | 'R' | 'A' | 'P' | 'H' | 'F';

/**
 * Maps a patient-space direction vector to an anatomical label — that of
 * the patient axis the direction is most aligned with, signed.
 * Uses DICOM LPS convention:
 *   +X = Left (L), -X = Right (R)
 *   +Y = Posterior (P), -Y = Anterior (A)
 *   +Z = Superior/Head (H), -Z = Inferior/Feet (F)
 */
export function directionLabel(direction: ReadonlyVec3): DirectionLabel {
    const abs0 = Math.abs(direction[0]);
    const abs1 = Math.abs(direction[1]);
    const abs2 = Math.abs(direction[2]);

    if (abs0 >= abs1 && abs0 >= abs2) {
        return direction[0] > 0 ? 'L' : 'R';
    }
    if (abs1 >= abs0 && abs1 >= abs2) {
        return direction[1] > 0 ? 'P' : 'A';
    }
    return direction[2] > 0 ? 'H' : 'F';
}

const OPPOSITE_DIRECTION: Readonly<Record<DirectionLabel, DirectionLabel>> = {
    L: 'R',
    R: 'L',
    A: 'P',
    P: 'A',
    H: 'F',
    F: 'H',
};

/** The anatomical label of the direction opposite `label`. */
export function oppositeDirection(label: DirectionLabel): DirectionLabel {
    return OPPOSITE_DIRECTION[label];
}

/**
 * Compute the four directional labels [left, top, right, bottom]
 * for a viewport from the view's orientation matrix.
 *
 * The view orientation matrix columns represent view-X, view-Y,
 * view-Z axes in patient (DICOM LPS) space.
 *
 * Screen mapping:
 *   left edge  = -viewX direction in patient space
 *   right edge = +viewX direction in patient space
 *   top edge   = +viewY direction in patient space
 *   bottom edge = -viewY direction in patient space
 *
 * @returns [leftLabel, topLabel, rightLabel, bottomLabel]
 */
export function computeDirectionLabels(
    viewOrientation: ReadonlyMat3
): [DirectionLabel, DirectionLabel, DirectionLabel, DirectionLabel] {
    const viewX = vec3.fromValues(
        viewOrientation[0],
        viewOrientation[1],
        viewOrientation[2]
    );
    const viewY = vec3.fromValues(
        viewOrientation[3],
        viewOrientation[4],
        viewOrientation[5]
    );

    const negViewX = vec3.negate(vec3.create(), viewX);
    const negViewY = vec3.negate(vec3.create(), viewY);

    return [
        directionLabel(negViewX), // left edge
        directionLabel(viewY), // top edge
        directionLabel(viewX), // right edge
        directionLabel(negViewY), // bottom edge
    ];
}

/**
 * Standard DICOM patient positions for axis-aligned transversal
 * acquisitions.
 */
export type PatientPosition = 'HFS' | 'HFP' | 'FFS' | 'FFP';

/**
 * Orientation matrix of each standard transversal patient position (DICOM LPS
 * convention: +X patient left, +Y posterior, +Z head). Column-major, so
 * column 0 is the ImageOrientationPatient row direction, column 1 its
 * column direction and column 2 their cross product (the slice normal).
 *
 * Each is derived from HFS by the rigid rotation that produces that
 * setup. Prone is a 180° turn about patient Z, which flips the row and
 * the column direction and leaves the normal alone; feet-first is a
 * 180° turn about the patient A-P axis, which flips the row direction
 * and the normal and leaves the column direction alone:
 *
 *   HFS: row +X, col +Y → normal +Z (identity)
 *   HFP: HFS turned 180° about patient Z (prone)
 *        row -X, col -Y → normal +Z
 *   FFS: HFS turned 180° about the patient A-P axis (feet-first)
 *        row -X, col +Y → normal -Z
 *   FFP: FFS turned 180° about patient Z
 *        row +X, col -Y → normal -Z
 *
 * Only exact axis-aligned transversal setups appear here; decubitus,
 * non-transversal acquisitions and any oblique matrix are outside this
 * table by construction.
 */
export const PATIENT_POSITION_ORIENTATIONS: ReadonlyArray<
    readonly [PatientPosition, mat3]
> = [
    ['HFS', mat3.fromValues(1, 0, 0, 0, 1, 0, 0, 0, 1)],
    ['HFP', mat3.fromValues(-1, 0, 0, 0, -1, 0, 0, 0, 1)],
    ['FFS', mat3.fromValues(-1, 0, 0, 0, 1, 0, 0, 0, -1)],
    ['FFP', mat3.fromValues(1, 0, 0, 0, -1, 0, 0, 0, -1)],
];

/**
 * Orientation matrix of a standard transversal patient position — the inverse
 * of {@link patientPositionFromOrientation}. Returns a fresh matrix, so
 * a caller cannot mutate the shared table.
 *
 * @param position One of the four standard transversal positions.
 */
export function orientationFromPatientPosition(
    position: PatientPosition
): mat3 {
    const entry = PATIENT_POSITION_ORIENTATIONS.find(
        ([candidate]) => candidate === position
    );
    if (entry === undefined) {
        throw new Error(
            `No orientation matrix for patient position '${position}'`
        );
    }
    return mat3.clone(entry[1]);
}

/**
 * Derive the DICOM PatientPosition tag value from a Grid's
 * orientation matrix. Returns the matching position string
 * for the four standard transversal orientations (HFS, HFP, FFS,
 * FFP), or `null` for oblique/non-standard orientations.
 *
 * @param orientation 3x3 orientation matrix (from
 *   `Grid.getOrientation()` or DICOM ImageOrientationPatient).
 * @param tolerance Component-wise comparison tolerance.
 */
export function patientPositionFromOrientation(
    orientation: ReadonlyMat3,
    tolerance = 1e-4
): PatientPosition | null {
    for (const [position, expected] of PATIENT_POSITION_ORIENTATIONS) {
        let match = true;
        for (let i = 0; i < 9; i++) {
            if (Math.abs(orientation[i] - expected[i]) > tolerance) {
                match = false;
                break;
            }
        }
        if (match) {
            return position;
        }
    }
    return null;
}
